import type { TypographyProps as MuiTypographyProps } from '@mui/material/Typography';
import MuiTypography from '@mui/material/Typography';
import is from '@sindresorhus/is';
import type { FunctionComponent, JSX, PropsWithChildren } from 'react';

type HeadlineComponent = Extract<MuiTypographyProps['variant'], keyof JSX.IntrinsicElements>;

type Color = MuiTypographyProps['color'];

type HeadlineProps = {
    readonly color?: Color;
    readonly component?: HeadlineComponent;
    readonly variant: HeadlineComponent | 'subtitle1' | 'subtitle2';
};

type PropsWithColor = {
    readonly color?: Color;
};

export const Headline: FunctionComponent<PropsWithChildren<HeadlineProps>> = ({
    children,
    color,
    component,
    variant,
}) => {
    if (!is.undefined(component)) {
        return (
            <MuiTypography color={color} component={component} variant={variant}>
                {children}
            </MuiTypography>
        );
    }

    return (
        <MuiTypography color={color} variant={variant}>
            {children}
        </MuiTypography>
    );
};

export const MainHeadlineSubPage: FunctionComponent<PropsWithChildren> = ({ children }) => (
    <Headline color="text.primary" component="h1" variant="h3">
        {children}
    </Headline>
);

export const MainHeadlineSubPageBoxed: FunctionComponent<PropsWithChildren> = ({ children }) => (
    <Headline component="h1" variant="h4">
        {children}
    </Headline>
);

export const SubHeadline: FunctionComponent<PropsWithChildren<PropsWithColor>> = ({ children, color }) => (
    <Headline color={!is.undefined(color) ? color : 'text.secondary'} component="h2" variant="h5">
        {children}
    </Headline>
);

export const SubTitle: FunctionComponent<PropsWithChildren<PropsWithColor>> = ({ children, color }) => (
    <Headline color={!is.undefined(color) ? color : 'text.secondary'} component="h2" variant="subtitle1">
        {children}
    </Headline>
);

export const Subtitle2: FunctionComponent<PropsWithChildren<PropsWithColor>> = ({ children, color }) => (
    <Headline color={!is.undefined(color) ? color : 'text.primary'} component="h3" variant="subtitle2">
        {children}
    </Headline>
);

export const CardTitleShort: FunctionComponent<PropsWithChildren<PropsWithColor>> = ({ children, color }) => (
    <MuiTypography color={!is.undefined(color) ? color : 'text.primary'} component="h1" noWrap={true} variant="h5">
        {children}
    </MuiTypography>
);

export const CardTitle: FunctionComponent<PropsWithChildren<PropsWithColor>> = ({ children, color }) => (
    <Headline color={!is.undefined(color) ? color : 'text.primary'} component="h1" variant="h4">
        {children}
    </Headline>
);

const captionTypeColor = {
    _default: 'inherit',
    learningOpportunity: 'learningOpportunity.main',
    light: 'text.secondary',
    primary: 'primary',
} as const;

type CaptionProps = PropsWithChildren<{
    readonly sx?: MuiTypographyProps['sx'];
    readonly type?: keyof typeof captionTypeColor;
}>;

export const Caption: FunctionComponent<CaptionProps> = ({ children, sx, type = '_default' }) => (
    <MuiTypography aria-label="Caption" color={captionTypeColor[type]} sx={sx} variant="caption">
        {children}
    </MuiTypography>
);

type TextProps = {
    readonly color?: Color;
};

export const Text: FunctionComponent<PropsWithChildren<TextProps & { readonly noWrap?: boolean }>> = ({
    children,
    color,
    noWrap,
}) => (
    <MuiTypography color={color} noWrap={noWrap} variant="body1">
        {children}
    </MuiTypography>
);

export const SmallText: FunctionComponent<PropsWithChildren<TextProps & { readonly noWrap?: boolean }>> = ({
    children,
    color,
    noWrap,
}) => (
    <MuiTypography color={color} noWrap={noWrap} variant="body2">
        {children}
    </MuiTypography>
);
